import { isString, isNull, isOptionalString } from './typeguards';

/**
 * @param {UserWebApi.SubscriptionType} type
 * @returns {boolean}
 */
function isPremiumPlus(type) {
    return type.toUpperCase() === 'PREMIUM_PLUS';
}

/**
 * @param {any} plan
 * @returns {plan is UserWebApi.SubscriptionPlan}
 */
function isValidSubscriptionPlan(plan) {
    /** @type {UserWebApi.SubscriptionPlan[]} */
    const plans = ['STANDARD', 'MILITARY', 'STUDENT', 'FRIEND', 'FAMILY', 'FIRST_RESPONDER'];

    return plans.includes(plan);
}

/**
 * @param {any} type
 * @returns {type is UserWebApi.SubscriptionType}
 */
function isValidSubscriptionType(type) {
    /** @type {UserWebApi.SubscriptionType[]} */
    const types = ['INTRO', 'PREMIUM', 'PREMIUM_PLUS', 'PROFESSIONAL'];

    return types.includes(type);
}

/**
 * @param {any} value
 * @returns {value is OfferWeb.StandardSubscriptionTypes}
 */
export function isStandardSubscriptionType(value) {
    /** @type {OfferWeb.StandardSubscriptionTypes[]} */
    const standardSubscriptionTypes = ['INTRO', 'PREMIUM', 'PREMIUM_PLUS'];

    return standardSubscriptionTypes.includes(value);
}

/**
 *
 * @param {any} value
 * @returns {value is UserWebApi.PeriodLength}
 */
function isValidPeriodLength(value) {
    /** @type {UserWebApi.PeriodLength[]} */
    const lengths = ['ONE_DAY', 'THREE_DAYS', 'ONE_WEEK', 'TWO_WEEKS', 'ONE_MONTH', 'THREE_MONTHS', 'SIX_MONTHS', 'ONE_YEAR'];

    return lengths.includes(value);
}

/**
 * @param {import('@tidal/userwebapi-sdk').com_tidal_api_userweb_pojo_subscription_prices_SubscriptionPriceDetails[]} details
 * @returns {OfferWeb.SubscriptionPriceDetail[]}
 */
function getSubscriptionPriceDetails(details) {
    const result = [];

    for (const detail of details) {
        if (
            detail &&
            isString(detail.countryCode) &&
            isString(detail.currency) &&
            isValidSubscriptionType(detail.subscriptionType) &&
            isValidSubscriptionPlan(detail.subscriptionPlan) &&
            isOptionalString(detail.description) &&
            (
                isOptionalString(detail.partnerProductType) ||
                isNull(detail.partnerProductType)
            ) &&
            isValidPeriodLength(detail.periodLength)
        ) {
            result.push({
                countryCode: detail.countryCode,
                currency: detail.currency,
                price: detail.price,
                subscriptionType: detail.subscriptionType,
                subscriptionPlan: detail.subscriptionPlan,
                description: detail.description,
                partnerProductType: detail.partnerProductType,
                periodLength: detail.periodLength,
            });
        }
    }

    return result;
}

/**
 * @param {any} query
 * @return {UserWebApi.SubscriptionPlan | undefined}
 */
function getSubscriptionPlanFromQuery(query) {
    if (typeof query !== 'string') {
        return;
    }

    const upperCaseQuery = query.toUpperCase();
    return isValidSubscriptionPlan(upperCaseQuery) ? upperCaseQuery : undefined;
}

/**
 * @param {any} query
 * @return {UserWebApi.SubscriptionType | undefined}
 */
function getSubscriptionTypeFromQuery(query) {
    if (typeof query !== 'string') {
        return;
    }

    const upperCaseQuery = query.toUpperCase();
    return isValidSubscriptionType(upperCaseQuery) ? upperCaseQuery : undefined;
}

/**
 * @param {UserWebApi.SubscriptionType} subscriptionType1
 * @param {UserWebApi.SubscriptionType} subscriptionType2
 * @returns {boolean}
 */
function isSameSubscriptionType(subscriptionType1, subscriptionType2) {
    if (subscriptionType1.toUpperCase() === subscriptionType2.toUpperCase()) {
        return true;
    }

    if (isPremiumPlus(subscriptionType1) && isPremiumPlus(subscriptionType2)) {
        return true;
    }

    return false;
}

/**
 * @param {UserWebApi.SubscriptionPlan | null | undefined} onlyShowPlan
 * @param {UserWebApi.SubscriptionType | null | undefined} onlyShowType
 */
function getOnlyPlanTypeFilter(onlyShowPlan, onlyShowType) {
    return /** @param {OfferWeb.SubscriptionProductProps} product */ product => {
        if (isValidSubscriptionPlan(onlyShowPlan) && onlyShowPlan.toUpperCase() !== product.subscriptionPlan) {
            return false;
        }
        if (isValidSubscriptionType(onlyShowType) && !isSameSubscriptionType(onlyShowType, product.subscriptionType)) {
            return false;
        }
        return true;
    };
}

/**
 * @type {Record<UserWebApi.SubscriptionType, string>}
 */
const typeNameMap = {
    INTRO: 'webCommon.tier.free',
    PREMIUM: 'webCommon.tier.premium',
    PREMIUM_PLUS: 'webCommon.tier.hifi',

    // Deprecated. These exist for compatibility with UserWebApi.SubscriptionType
    BASIC: '',
    PROFESSIONAL: '',
    PREMIUM_MID: '',
};

/**
 * @type {Record<UserWebApi.SubscriptionPlan, string>}
 */
const planNameMap = {
    STANDARD: 'webCommon.plan.standard',
    FAMILY: 'webCommon.plan.family',
    STUDENT: 'webCommon.plan.student',
    MILITARY: 'webCommon.plan.military',
    FIRST_RESPONDER: 'webCommon.plan.firstResponder',

    // Deprecated. These exist for compatibility with UserWebApi.SubscriptionPlan
    FAMILY_OLD: '',
    FRIEND: '',
};

/**
 * Get the official full subscription name for the given subscription type and plan
 *
 * @param {UserWebApi.SubscriptionType} type
 * @param {UserWebApi.SubscriptionPlan} plan
 * @param {function} lang
 * @returns {string}
 */
function getSubscriptionName(type, plan, lang) {
    const typeNameKey = typeNameMap[type];
    const planNameKey = planNameMap[plan];
    return `${lang(typeNameKey)} ${lang(planNameKey)}`;
}

/**
 * Get the official full subscription type name for the given subscription type
 *
 * @param {UserWebApi.SubscriptionType} type
 * @param {function} lang
 * @returns {string}
 */
function getSubscriptionTypeName(type, lang) {
    return lang(typeNameMap[type]);
}

/**
 * Get the translation key for the official full subscription type name for the given subscription type
 *
 * @param {UserWebApi.SubscriptionType} type
 * @returns {string}
 */
function getSubscriptionTypeNameKey(type) {
    return typeNameMap[type];
}

/**
 * Get the official plan name for the given subscription plan
 *
 * @param {UserWebApi.SubscriptionPlan} plan
 * @param {function} lang
 * @returns {string}
 */
function getSubscriptionPlanName(plan, lang) {
    return lang(planNameMap[plan]);
}

export {
    isPremiumPlus,
    isSameSubscriptionType,
    isValidSubscriptionPlan,
    isValidSubscriptionType,
    getOnlyPlanTypeFilter,
    getSubscriptionPlanFromQuery,
    getSubscriptionTypeFromQuery,
    isValidPeriodLength,
    getSubscriptionPriceDetails,
    getSubscriptionName,
    getSubscriptionTypeName,
    getSubscriptionTypeNameKey,
    getSubscriptionPlanName,
};
